import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { SpinnerLoader } from '../Loading';
import { Icon } from './icon';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center overflow-hidden whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Default variant (grayscale)
        'default': 'bg-action text-background hover:bg-action-hover active:bg-action-selected [&_svg]:text-background',

        // Text variants
        'text': 'text-text hover:bg-text-secondary/10 active:bg-text-secondary/20 disabled:text-text/50',
        'text-primary': 'text-primary hover:bg-text-primary/10 active:bg-text-primary/20',
        'text-secondary': 'text-secondary hover:bg-text-secondary/10 active:bg-text-secondary/20',

        // Primary variants
        'primary': 'bg-primary text-primary-foreground hover:bg-primary-dark active:bg-primary-light',
        'primary-light': 'bg-primary-light text-primary-foreground hover:bg-primary active:bg-primary-dark',
        'primary-dark': 'bg-primary-dark text-primary-foreground hover:bg-primary active:bg-primary-light',

        // Secondary variants
        'secondary': 'bg-secondary text-secondary-foreground hover:bg-action/10-dark active:bg-action/20-light',
        'secondary-light': 'bg-secondary-light text-secondary-foreground hover:bg-action/10 active:bg-action/20-dark',
        'secondary-dark': 'bg-secondary-dark text-secondary-foreground hover:bg-action/10 active:bg-action/20-light',

        // Info variants
        'info': 'bg-info text-info-foreground hover:bg-info-dark active:bg-info-light',
        'info-light': 'bg-info-light text-info-foreground hover:bg-info active:bg-info-dark',
        'info-dark': 'bg-info-dark text-info-foreground hover:bg-info active:bg-info-light',

        // Success variants
        'success': 'bg-success text-success-foreground hover:bg-success-dark active:bg-success-light',
        'success-light': 'bg-success-light text-success-foreground hover:bg-success active:bg-success-dark',
        'success-dark': 'bg-success-dark text-success-foreground hover:bg-success active:bg-success-light',

        // Warning variants
        'warning': 'bg-warning text-warning-foreground hover:bg-warning-dark active:bg-warning-light',
        'warning-light': 'bg-warning-light text-warning-foreground hover:bg-warning active:bg-warning-dark',
        'warning-dark': 'bg-warning-dark text-warning-foreground hover:bg-warning active:bg-warning-light',

        // Error variants
        'error': 'bg-error text-error-foreground hover:bg-error-dark active:bg-error-light',
        'text-error': 'text-error hover:bg-text-error/10 active:bg-text-error/20',
        'error-light': 'bg-error-light text-error-foreground hover:bg-error active:bg-error-dark',
        'error-dark': 'bg-error-dark text-error-foreground hover:bg-error active:bg-error-light',

        // Action variants
        'action': 'bg-action text-background hover:bg-action-hover active:bg-action-selected',
        'action-selected': 'bg-action-selected text-primary hover:bg-action-hover',

        // Outline variants
        'outline': 'border-[1px] border-input bg-background hover:bg-action/10 active:bg-action/20',
        'outline-primary': 'border-[1px] border-primary text-primary hover:bg-primary/10 active:bg-primary/20',
        'outline-secondary': 'border-[1px] border-secondary text-secondary hover:bg-action/10/10 active:bg-action/20/20',
        'outline-info': 'border-[1px] border-info text-info hover:bg-info/10 active:bg-info/20',
        'outline-success': 'border-[1px] border-success text-success hover:bg-success/10 active:bg-success/20',
        'outline-warning': 'border-[1px] border-warning text-warning hover:bg-warning/10 active:bg-warning/20',
        'outline-error': 'border-[1px] border-error text-error hover:bg-error/10 active:bg-error/20',

        // Ghost variants
        'ghost': 'text-muted-foreground hover:bg-action/10 active:bg-action/20',
        'ghost-primary': 'text-primary hover:bg-primary/10 hover:text-primary active:bg-primary/20 active:text-primary',
        'ghost-secondary': 'text-secondary hover:bg-secondary/10 hover:text-secondary active:bg-secondary/20 active:text-secondary',
        'ghost-info': 'text-info hover:bg-info/10 hover:text-info active:bg-info/20 active:text-info',
        'ghost-success': 'text-success hover:bg-success/10 hover:text-success active:bg-success/20 active:text-success',
        'ghost-warning': 'text-warning hover:bg-warning/10 hover:text-warning active:bg-warning/20 active:text-warning',
        'ghost-error': 'text-error hover:bg-error/10 hover:text-error active:bg-error/20 active:text-error',

        // Link variants
        'link': 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        'default': 'h-10 px-6 py-2',
        'sm': 'h-8 px-4 py-1.5 text-xs',
        'lg': 'h-12 px-8 py-3 text-base',
        'icon': 'h-10 w-10 p-2',
        'icon-sm': 'h-8 w-8 p-1.5',
        'icon-xs': 'h-6 w-6 p-1',
        'icon-lg': 'h-12 w-12 p-2.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = {
  asChild?: boolean;
  loading?: boolean;
  startIcon?: string;
  endIcon?: string;
  disableRipple?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  tooltipAlign?: 'start' | 'center' | 'end';
} & React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

function Button({ ref, className, variant, size, asChild = false, loading = false, startIcon, endIcon, disabled = false, disableRipple = false, tooltipContent, tooltipSide = 'top', tooltipAlign = 'center', children, ...props }: ButtonProps & { ref?: React.RefObject<HTMLButtonElement | null> }) {
  const isDisabled = loading === true || disabled === true;

  const createRipple = (
    e: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement>,
    centered = false,
  ) => {
    if (disableRipple === true || isDisabled === true) {
      return;
    }; // Skip ripple if disableRipple is true or button is disabled

    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    if (centered) {
      ripple.style.left = `${button.clientWidth / 2 - radius}px`;
      ripple.style.top = `${button.clientHeight / 2 - radius}px`;
    } else {
      ripple.style.left = `${(e as React.MouseEvent).clientX - button.offsetLeft - radius}px`;
      ripple.style.top = `${(e as React.MouseEvent).clientY - button.offsetTop - radius}px`;
    }

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.classList.add('ripple');

    const rippleContainer = button.getElementsByClassName('ripple-container')[0];
    if (rippleContainer !== null && rippleContainer !== undefined) {
      rippleContainer.appendChild(ripple);
    }

    setTimeout(() => {
      ripple.remove();
    }, 800); // Adjusted ripple duration
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    createRipple(e); // Conditionally trigger ripple on click
  };

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    createRipple(e, true); // Conditionally trigger centered ripple on focus
  };

  const iconSize = {
    'icon-xs': 16,
    'icon-sm': 20,
    'icon': 24,
    'icon-lg': 28,
    'sm': 16,
    'default': 20,
    'lg': 24,
  }[size || 'default'];

  const buttonContent = asChild
    ? (
      <Slot
        onClick={isDisabled === true ? undefined : handleClick}
        onFocus={isDisabled === true ? undefined : handleFocus}
        className={cn(
          buttonVariants({ variant, size, className }),
          'ripple-button',
          size?.startsWith('icon') === true && 'icon-button',
          isDisabled === true && 'disabled',
          'inline-flex items-center gap-2',
        )}
        ref={ref}
        {...props}
      >
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<{ className?: string; children?: React.ReactNode }>, {
            className: cn(
              (children as React.ReactElement<{ className?: string; children?: React.ReactNode }>).props?.className,
              'relative inline-flex items-center justify-center gap-2',
            ),
            children: (
              <>
                <span className="ripple-container absolute inset-0 overflow-hidden"></span>
                {startIcon !== undefined && startIcon !== null && loading === false && (
                  <div className="shrink-0">
                    <Icon
                      icon={startIcon}
                      size={iconSize}
                      className="inherit"
                    />
                  </div>
                )}
                {loading === true && (
                  <SpinnerLoader
                    variant="inherit"
                    size={iconSize}
                  />
                )}
                {(children as React.ReactElement<{ className?: string; children?: React.ReactNode }>).props?.children}
                {endIcon !== undefined && endIcon !== null && loading === false && (
                  <div className="shrink-0">
                    <Icon
                      icon={endIcon}
                      size={iconSize}
                      className="inherit"
                    />
                  </div>
                )}
              </>
            ),
          })
          : children}
      </Slot>
    )
    : (
      <button
        type="button"
        onClick={isDisabled === true ? undefined : handleClick}
        onFocus={isDisabled === true ? undefined : handleFocus}
        disabled={isDisabled}
        className={cn(
          buttonVariants({ variant, size, className }),
          'ripple-button',
          size?.startsWith('icon') === true && 'icon-button',
          isDisabled === true && 'disabled',
          'inline-flex items-center gap-2',
        )}
        ref={ref}
        {...props}
      >
        <span className="ripple-container absolute inset-0 overflow-hidden"></span>
        {startIcon !== undefined && startIcon !== null && loading === false && (
          <div className="shrink-0">
            <Icon
              icon={startIcon}
              size={iconSize}
              className="inherit"
            />
          </div>
        )}
        {loading === true && (
          <SpinnerLoader
            variant="inherit"
            size={iconSize}
          />
        )}
        {children}
        {endIcon !== undefined && endIcon !== null && loading === false && (
          <div className="shrink-0">
            <Icon
              icon={endIcon}
              size={iconSize}
              className="inherit"
            />
          </div>
        )}
      </button>
    );

  // If tooltipContent is provided, wrap button in Tooltip
  if (tooltipContent !== undefined && tooltipContent !== null) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{buttonContent}</span>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide} align={tooltipAlign}>
            {typeof tooltipContent === 'string'
              ? (
                <p className="whitespace-pre-line">{tooltipContent}</p>
              )
              : (
                tooltipContent
              )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
}

Button.displayName = 'Button';

export { Button, buttonVariants };
